/**
 * @file CandidatesList.jsx
 * @author Archana
 * @date 2024-10-24
 * @description Show the list of candidates
 */
import React from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton, 
} from "@mui/material";
import { ContentPasteSearchOutlined, Visibility, SearchOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

export const CandidatesList = ({ isLoading, leads, searchByPhone }) => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchedPhone, setSearchedPhone] = React.useState("");

  const role = useSelector((state) => state.auth.user?.role);

  const filteredUsers = leads.filter(
    (lead) =>
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent="space-between"
      >
        <Stack direction={"row"} alignItems={"center"} columnGap={2}>
          {isLoading ? (
            <Skeleton variant="rounded" width={490} height={24} />
          ) : (
            <Typography fontWeight={700} fontSize={24} color="#222222">
              Candidate
            </Typography>
          )}
          {!isLoading && (
            <TextField
              id="outlined-start-adornment"
              placeholder="Search candidates"
              className="inputRounded"
              color="black"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContentPasteSearchOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          {!isLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                id="outlined-start-adornment"
                placeholder="Search with phone"
                className="inputRounded"
                color="black"
                value={searchedPhone}
                onChange={(e) => {
                  // Allow only numbers and max length of 10
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value.length <= 10) {
                    setSearchedPhone(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    searchByPhone(searchedPhone);
                  }
                }}
                inputProps={{ maxLength: 10 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ContentPasteSearchOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <IconButton 
                onClick={() => searchByPhone(searchedPhone)}
                sx={{
                  bgcolor: '#3A80F7',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#2563eb',
                  }
                }}
              >
                <SearchOutlined  />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          pb: 1,
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 2 }}
            width="100%"
            height={400}
          />
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "#3A80F7",
                  }}
                >
                  <TableCell sx={{ color: "white", borderTopLeftRadius: 8 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>ID</TableCell>
                  <TableCell sx={{ color: "white" }}>Email</TableCell>
                  <TableCell sx={{ color: "white" }}>Conversion Date</TableCell>
                  <TableCell sx={{ color: "white", borderTopRightRadius: 8 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((lead) => (
                    <TableRow key={lead.candidateuuid}>
                      <TableCell>{lead.name}</TableCell>
                      <TableCell>CR{lead.id}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead?.leadconversiondate
                          ? dayjs(lead.leadconversiondate).format("LL")
                          : ""}</TableCell>
                      <TableCell>
                        {role === 'agent'?(
                          <Button variant="outlined"
                            onClick={() =>
                              navigate(
                                `/candidates/candidate-lifecycle-agent/${lead.candidateuuid}`,
                                {
                                  state: { lead: lead },
                                }
                              )
                            }
                            style={{ backgroundColor: "#FCF2DF", color: "black", borderWidth: 0.5, borderColor: "#BD8623", textTransform: "none" }}>
                            View Lifecycle
                          </Button>
                        ):(
                            <IconButton
                              onClick={() =>
                                navigate(
                                  `/candidates/candidate-details/${lead.candidateuuid}`,
                                  {
                                    state: { lead: lead },
                                  }
                                )
                              }
                            >
                              <Visibility />
                            </IconButton>
                          )}
                          
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No candidates found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};
